// What Maviinci sells and where each product is listed. One file, copied
// byte-for-byte into the admin panel (admin/src/Data/taxonomy.js); run
// `node scripts/check-taxonomy-sync.js` after editing either copy.
//
//   department  a menu entry and listing page        /products/kurta
//   style       an indexable page inside it           /products/kurta/sherwani
//   filter      a style with page:false: a facet chip inside its parent page
//
// A product joins a style through its admin `category` (garments), its
// `accessoryType` (accessories), or a `styleTags` entry. See docs/catalog-plan.md.

// A style or collection page with fewer published products than this renders
// for shoppers but stays out of Google (noindex, not in the sitemap).
export const MIN_INDEXABLE = 3;

// sizing: what the order flow asks for.
//   body  garment measurements   head  head circumference
//   foot  A4 foot trace (mojari) none  nothing
export const SIZING = ["body", "head", "foot", "none"];

export const DEPARTMENTS = [
  {
    slug: "kurta",
    label: "Ethnic & Kurta",
    short: "Ethnic",
    styles: [
      {
        slug: "kurta-pajama", label: "Kurta Pajama", page: true, priority: 1,
        match: { categories: ["kurta"], notPieces: ["Sadri", "Coat"] },
        customizer: "/custom/ethnic/select_cloth/personalize", sizing: "body",
        title: "Kurta Pajama for Men, Made to Measure",
        h1: "Kurta Pajama Sets for Men",
        intro: "Kurta pajama sets cut to your measurements, in cotton, linen and silk, for festivals, pujas and family weddings.",
      },
      {
        slug: "kurta", label: "Kurta", page: true, priority: 1,
        match: { categories: ["only_kurta"] },
        customizer: "/custom/only_kurta/select_cloth/personalize", sizing: "body",
        title: "Kurta for Men, Stitched to Your Size",
        h1: "Kurtas for Men",
        intro: "Single kurtas in every length and collar, tailored to fit, to wear with your own pajama, jeans or churidar.",
      },
      {
        slug: "nehru-jacket", label: "Nehru Jacket", page: true, priority: 1,
        match: { categories: ["sadri"] },
        customizer: "/custom/sadri/select_cloth/personalize", sizing: "body",
        title: "Nehru Jacket for Men, Custom Sadri & Modi Jacket",
        h1: "Nehru Jackets & Sadris",
        intro: "Nehru jackets, sadris and Modi jackets in velvet, jacquard and silk blends, tailored to sit right over a kurta.",
      },
      {
        slug: "kurta-jacket-set", label: "Kurta Jacket Set", page: true, priority: 1,
        match: { categories: ["kurta"], pieces: ["Sadri", "Coat"] },
        customizer: "/custom/ethnic/select_cloth/personalize", sizing: "body",
        title: "Kurta Jacket Set for Men, Wedding & Festive",
        h1: "Kurta Jacket Sets",
        intro: "Kurta, pajama and a matching Nehru jacket as one set, the easiest way to dress for a wedding as a guest.",
      },
      {
        slug: "sherwani", label: "Sherwani", page: true, priority: 1,
        match: { categories: ["sherwani"] },
        customizer: null, sizing: "body",
        title: "Sherwani for Men & Groom, Made to Measure",
        h1: "Sherwanis for Men",
        intro: "Groom and wedding sherwanis with hand embroidery, cut to your measurements and delivered across India.",
      },
      {
        slug: "achkan", label: "Achkan", page: false, parent: "sherwani",
        match: { styleTags: ["achkan"] }, sizing: "body",
      },
      {
        slug: "pathani", label: "Pathani", page: true, priority: 2,
        match: { categories: ["pathani"] },
        customizer: null, sizing: "body",
        title: "Pathani Suit for Men, Made to Measure",
        h1: "Pathani Suits",
        intro: "Pathani kurtas with salwar, collared and full-sleeved, stitched to your size for Eid and everyday wear.",
      },
      {
        slug: "indo-western", label: "Indo-Western", page: true, priority: 2,
        match: { categories: ["indo_western"] },
        customizer: null, sizing: "body",
        title: "Indo-Western Outfits for Men, Custom Tailored",
        h1: "Indo-Western Wear",
        intro: "Indo-western sets for sangeet and reception, with asymmetric cuts and drapes, tailored to you.",
      },
      {
        slug: "pajama", label: "Pajama & Churidar", page: true, priority: 2,
        match: { categories: ["pajama"] },
        customizer: "/custom/pajama/select_cloth/personalize", sizing: "body",
        title: "Pajama, Churidar & Salwar for Men",
        h1: "Pajamas & Churidars",
        intro: "Churidar, straight pajama, salwar, dhoti and aligarhi bottoms, cut to your measurements to go with any kurta.",
      },
      {
        slug: "dhoti-kurta", label: "Dhoti Kurta", page: false, parent: "kurta-pajama",
        match: { styleTags: ["dhoti"] }, sizing: "body",
      },
      {
        slug: "jubba", label: "Jubba", page: true, priority: 3,
        match: { categories: ["jubba"] },
        customizer: null, sizing: "body",
        title: "Jubba for Men, Custom Stitched",
        h1: "Jubbas",
        intro: "Full-length jubbas in cotton and blends, stitched to your size for Eid, Jumma and daily wear.",
      },
    ],
  },
  {
    slug: "suit",
    label: "Suits & Blazers",
    short: "Suits",
    styles: [
      {
        slug: "suit", label: "Suit", page: true, priority: 1,
        match: { categories: ["suit"] },
        customizer: "/custom/suit/select_cloth/personalize", sizing: "body",
        title: "Suits for Men, Made to Measure 2 & 3 Piece",
        h1: "Men's Suits",
        intro: "Two-piece and three-piece suits cut to your measurements in wool and wool blends, for weddings and work.",
      },
      {
        slug: "blazer", label: "Blazer", page: true, priority: 1,
        match: { categories: ["blazer"] },
        customizer: "/custom/blazer/select_cloth/personalize", sizing: "body",
        title: "Blazer for Men, Custom Tailored",
        h1: "Blazers for Men",
        intro: "Single and double-breasted blazers tailored to your shoulders and chest, for parties, receptions and the office.",
      },
      {
        slug: "jodhpuri", label: "Jodhpuri / Bandhgala", page: true, priority: 1,
        match: { categories: ["jodhpuri"] },
        customizer: "/custom/suit/select_cloth/personalize", sizing: "body",
        title: "Jodhpuri Suit & Bandhgala for Men, Made to Measure",
        h1: "Jodhpuri Suits & Bandhgalas",
        intro: "Jodhpuri suits and bandhgalas with a closed collar and royal finish, cut to your size for weddings and receptions.",
      },
      {
        slug: "tuxedo", label: "Tuxedo", page: true, priority: 2,
        match: { categories: ["tuxedo"] },
        customizer: "/custom/suit/select_cloth/personalize", sizing: "body",
        title: "Tuxedo for Men, Custom Tailored",
        h1: "Tuxedos",
        intro: "Satin-lapel tuxedos and dinner jackets tailored to you, for receptions and black-tie evenings.",
      },
      {
        slug: "waistcoat", label: "Waistcoat", page: true, priority: 2,
        match: { categories: ["vestcoat"] },
        customizer: "/custom/vestcoat/select_cloth/personalize", sizing: "body",
        title: "Waistcoat for Men, Made to Measure",
        h1: "Waistcoats",
        intro: "Waistcoats to wear with a suit or on their own over a shirt, cut to your measurements.",
      },
      {
        slug: "safari-suit", label: "Safari Suit", page: true, priority: 2,
        match: { categories: ["safari"] },
        customizer: null, sizing: "body",
        title: "Safari Suit for Men, Custom Stitched",
        h1: "Safari Suits",
        intro: "Classic safari suits with patch pockets and a belted waist, stitched to your size for office and official wear.",
      },
    ],
  },
  {
    slug: "formal",
    label: "Shirts & Trousers",
    short: "Formal",
    styles: [
      {
        slug: "shirt", label: "Formal Shirt", page: true, priority: 2,
        match: { categories: ["shirt"] },
        customizer: "/custom/shirt/select_cloth/personalize", sizing: "body",
        title: "Formal Shirts for Men, Made to Measure",
        h1: "Formal Shirts",
        intro: "Formal shirts in cotton and linen with your choice of collar and cuff, cut to your measurements.",
      },
      {
        slug: "trouser", label: "Trouser", page: true, priority: 2,
        match: { categories: ["pant"] },
        customizer: "/custom/pant/select_cloth/personalize", sizing: "body",
        title: "Formal Trousers for Men, Made to Measure",
        h1: "Formal Trousers",
        intro: "Formal trousers with flat or pleated fronts, tailored to your waist and length.",
      },
      {
        slug: "formal-set", label: "Formal Set", page: false, parent: "shirt",
        match: { categories: ["formal"] }, sizing: "body",
      },
    ],
  },
  {
    slug: "uniform",
    label: "Uniforms",
    short: "Uniforms",
    b2b: true,
    // /products/uniform: the landing copy, read by the React page
    // (src/Presets/index.js, UniformLanding.js) and the server render
    // (api/_lib/styleShell.js), so both always say the same thing.
    landing: {
      title: "Uniforms with Your Logo – Hotel, Corporate, School & More | Maviinci",
      description:
        "Uniforms tailored like suits for hotels, offices, hospitals, schools and factories. Order one piece or a thousand, with your logo embroidered or printed and approved first.",
      h1: "Uniforms for your team, with your logo",
      intro:
        "Uniforms tailored like suits, from the front desk to the kitchen, the clinic to the factory floor. Order one piece or a thousand, with your logo embroidered or printed and approved before we stitch.",
      sectorsTitle: "Who are you dressing?",
      stepsTitle: "From one piece to the whole team",
      steps: [
        { title: "Pick the uniform", copy: "Choose the garment and the sizes your team needs, from a single piece upward." },
        { title: "Add your logo", copy: "Upload it once, choose embroidery or print for each place, and add staff names if you like." },
        { title: "Approve the mockup", copy: "We send your logo on the garment on WhatsApp. Nothing is stitched until you say yes." },
        { title: "Stitched and delivered", copy: "In about 10 days from approval, anywhere in India, with a GST invoice." },
      ],
      ratesTitle: "Clear rates, no surprises",
      rates:
        "Every uniform shows its price for a single piece and its lower rate from 10 pieces. Your logo adds a small amount per piece and a one-time setup for the embroidery file or print die. The same logo at the same size in two places shares one setup. No logo, no logo charge.",
      tiers: [
        ["1–9 pieces", "Single-piece rate. A logo on even one piece."],
        ["10+ pieces", "Team rate on every piece, setup spread across the order."],
        ["50+ pieces", "A quote with fabric samples and a size plan for your staff."],
      ],
      ctaTitle: "Planning uniforms for a team?",
      cta: "Tell us the roles and numbers. We will suggest fabrics, send swatches and plan sizes with you, free.",
    },
    // A uniform is uploaded once with a list of `sectors` and shows on every
    // sector page in it. The categories are uniforms saved before sectors
    // existed. Spec: docs/uniform-plan.md.
    styles: [
      {
        slug: "hotel", label: "Hotel", page: true, priority: 2,
        match: { sectors: ["hotel"], categories: ["uniform_hotel", "apron"] }, sizing: "body",
        title: "Hotel Uniforms with Your Logo: Front Office to Kitchen",
        h1: "Hotel Uniforms",
        intro: "Front office blazers, bandhgalas for the doorman, chef coats and housekeeping tunics, tailored and finished with your logo.",
      },
      {
        slug: "restaurant", label: "Restaurant & Cafe", page: true, priority: 2,
        match: { sectors: ["restaurant"] }, sizing: "body",
        title: "Restaurant & Cafe Uniforms, Chef Coats and Aprons",
        h1: "Restaurant & Cafe Uniforms",
        intro: "Chef coats, aprons, waistcoats and polos for kitchen and floor staff, with your logo embroidered or printed.",
      },
      {
        slug: "hospital", label: "Hospital & Clinic", page: true, priority: 2,
        match: { sectors: ["hospital"], categories: ["uniform_hospital"] }, sizing: "body",
        title: "Hospital Uniforms: Scrubs, Lab Coats, Nurse Uniforms",
        h1: "Hospital & Clinic Uniforms",
        intro: "Scrubs, doctor coats, nurse uniforms and housekeeping tunics, made to last daily washing, with your hospital's logo.",
      },
      {
        slug: "corporate", label: "Corporate & Office", page: true, priority: 2,
        match: { sectors: ["corporate"], categories: ["uniform_corporate"] }, sizing: "body",
        title: "Corporate Uniforms with Your Logo, Tailored",
        h1: "Corporate & Office Uniforms",
        intro: "Shirts, trousers, blazers and suits for your team, tailored like a suit and finished with your logo.",
      },
      {
        slug: "school", label: "School & College", page: true, priority: 2,
        match: { sectors: ["school"], categories: ["uniform_school"] }, sizing: "body",
        title: "School Uniforms, Blazers and Ties with Your Logo",
        h1: "School & College Uniforms",
        intro: "Shirts, trousers, skirts, blazers and ties in your school's colours, with the crest embroidered.",
      },
      {
        slug: "factory", label: "Factory & Industrial", page: true, priority: 2,
        match: { sectors: ["factory"] }, sizing: "body",
        title: "Factory & Industrial Uniforms: Coveralls, Work Shirts",
        h1: "Factory & Industrial Uniforms",
        intro: "Coveralls, work shirts, cargo trousers and safety jackets for plants and warehouses, with your company logo.",
      },
      {
        slug: "security", label: "Security", page: true, priority: 2,
        match: { sectors: ["security"], categories: ["uniform_security"] }, sizing: "body",
        title: "Security Guard Uniforms with Your Agency Logo",
        h1: "Security Uniforms",
        intro: "Guard shirts and trousers, blazers for lobby security, caps and belts, in your agency's colours.",
      },
      {
        slug: "spa", label: "Salon & Spa", page: true, priority: 3,
        match: { sectors: ["spa"] }, sizing: "body",
        title: "Salon & Spa Uniforms, Tunics and Aprons",
        h1: "Salon & Spa Uniforms",
        intro: "Soft spa tunics, stylist aprons and capes, with your salon's logo.",
      },
      { slug: "event", label: "Event & Catering", page: false, parent: "hotel", match: { sectors: ["event"] }, sizing: "body" },
      { slug: "bank", label: "Bank & Showroom", page: false, parent: "corporate", match: { sectors: ["bank"] }, sizing: "body" },
      { slug: "transport", label: "Driver & Transport", page: false, parent: "corporate", match: { sectors: ["transport"] }, sizing: "body" },
    ],
  },
  {
    slug: "accessory",
    label: "Accessories",
    short: "Accessories",
    styles: [
      {
        slug: "safa", label: "Safa", page: true, priority: 1,
        match: { accessoryTypes: ["safa"] }, sizing: "head",
        title: "Safa & Pagdi for Groom, Wedding Turban",
        h1: "Safas & Pagdis",
        intro: "Wedding safas and pagdis, made to match your sherwani's colour.",
      },
      { slug: "kalgi", label: "Kalgi & Sarpech", page: false, parent: "safa", match: { accessoryTypes: ["kalgi", "sarpech"] }, sizing: "none" },
      { slug: "sehra", label: "Sehra", page: false, parent: "safa", match: { accessoryTypes: ["sehra"] }, sizing: "none" },
      {
        slug: "dupatta", label: "Dupatta & Stole", page: true, priority: 1,
        match: { accessoryTypes: ["dupatta"] }, sizing: "none",
        title: "Dupatta & Stole for Men, Sherwani Dupatta",
        h1: "Dupattas & Stoles",
        intro: "Groom dupattas and stoles to drape over a sherwani or kurta, matched to your outfit.",
      },
      { slug: "shawl", label: "Shawl", page: false, parent: "dupatta", match: { accessoryTypes: ["shawl"] }, sizing: "none" },
      {
        slug: "mala", label: "Mala", page: true, priority: 1,
        match: { accessoryTypes: ["mala"] }, sizing: "none",
        title: "Groom Mala & Sherwani Necklace",
        h1: "Malas",
        intro: "Pearl, kundan and bead malas to wear with a sherwani or bandhgala.",
      },
      {
        slug: "islamic-cap", label: "Islamic Cap", page: true, priority: 1,
        match: { accessoryTypes: ["cap"] }, sizing: "head",
        title: "Islamic Cap for Men, Namaz Topi & Kufi",
        h1: "Islamic Caps & Topis",
        intro: "Namaz topis, kufis and skull caps in cotton, knit and embroidered styles, sized to your head.",
      },
      {
        slug: "mojari", label: "Mojari", page: true, priority: 1,
        match: { accessoryTypes: ["mojari"] }, sizing: "foot",
        title: "Mojari for Men, Made to Your Foot",
        h1: "Mojaris & Juttis",
        intro: "Embroidered mojaris made to a tracing of your foot, so they fit from the first wear.",
      },
      {
        slug: "pocket-square", label: "Pocket Square", page: true, priority: 1,
        match: { accessoryTypes: ["pocket_square"] }, sizing: "none",
        title: "Pocket Squares for Men",
        h1: "Pocket Squares",
        intro: "Pocket squares in silk and cotton, or cut from the same fabric as your suit or kurta.",
      },
      { slug: "handkerchief", label: "Handkerchief", page: false, parent: "pocket-square", match: { accessoryTypes: ["handkerchief"] }, sizing: "none" },
      {
        slug: "brooch", label: "Brooch", page: true, priority: 2,
        match: { accessoryTypes: ["brooch"] }, sizing: "none",
        title: "Brooch for Men, Sherwani & Jacket Brooch",
        h1: "Brooches",
        intro: "Brooches for sherwanis, bandhgalas and Nehru jackets.",
      },
      { slug: "lapel-pin", label: "Lapel Pin", page: false, parent: "brooch", match: { accessoryTypes: ["lapel_pin"] }, sizing: "none" },
      {
        slug: "tie", label: "Tie", page: true, priority: 2,
        match: { accessoryTypes: ["tie"] }, sizing: "none",
        title: "Ties for Men, Silk & Suit-Fabric Ties",
        h1: "Ties",
        intro: "Silk ties, and ties cut from your suit's own fabric.",
      },
      { slug: "bow-tie", label: "Bow Tie", page: false, parent: "tie", match: { accessoryTypes: ["bow_tie"] }, sizing: "none" },
      { slug: "tie-pin", label: "Tie Pin", page: false, parent: "tie", match: { accessoryTypes: ["tie_pin"] }, sizing: "none" },
      {
        slug: "cufflinks", label: "Cufflinks", page: true, priority: 2,
        match: { accessoryTypes: ["cufflinks"] }, sizing: "none",
        title: "Cufflinks for Men",
        h1: "Cufflinks",
        intro: "Cufflinks for formal shirts, suits and tuxedos.",
      },
      {
        slug: "caps-hats", label: "Caps & Hats", page: true, priority: 2,
        match: { accessoryTypes: ["hat"] }, sizing: "head",
        title: "Caps & Hats for Men",
        h1: "Caps & Hats",
        intro: "Caps and hats in cotton, wool and felt, sized to your head.",
      },
      {
        slug: "gamchha", label: "Gamchha", page: true, priority: 2,
        match: { accessoryTypes: ["gamchha"] }, sizing: "none",
        title: "Cotton Gamchha for Men",
        h1: "Gamchhas",
        intro: "Handloom cotton gamchhas in classic checks and colours.",
      },
      {
        slug: "shemagh", label: "Shemagh / Arab Rumal", page: true, priority: 2,
        match: { accessoryTypes: ["shemagh"] }, sizing: "none",
        title: "Shemagh, Keffiyeh & Arab Rumal for Men",
        h1: "Shemaghs & Arab Rumals",
        intro: "Shemaghs, keffiyehs and Arab rumals, the Arabian gamchha, in cotton and blends.",
      },
      {
        slug: "gift-sets", label: "Gift Sets", page: true, priority: 2,
        match: { accessoryTypes: ["gift_set"] }, sizing: "none",
        title: "Groom Kits & Gift Sets for Men",
        h1: "Gift Sets",
        intro: "Groom kits, suit essentials boxes and sets matched to your outfit's fabric.",
      },
      { slug: "kamarbandh", label: "Kamarbandh", page: false, parent: "dupatta", match: { accessoryTypes: ["kamarbandh"] }, sizing: "none" },
      { slug: "suspenders", label: "Suspenders", page: false, parent: "tie", match: { accessoryTypes: ["suspenders"] }, sizing: "none" },
      { slug: "socks", label: "Socks", page: false, parent: "pocket-square", match: { accessoryTypes: ["socks"] }, sizing: "none" },
      { slug: "belt", label: "Belt", page: false, parent: "tie", match: { accessoryTypes: ["belt"] }, sizing: "none" },
    ],
  },
];

// Occasion collections, filled from the tags products already carry.
export const COLLECTIONS = [
  {
    slug: "groom-wear", label: "Groom Wear", priority: 1,
    match: { roles: ["groom"] },
    customizer: null,
    title: "Groom Wear: Sherwani, Jodhpuri, Safa & More",
    h1: "Groom Wear",
    intro: "Everything for the groom, from sherwani and jodhpuri to safa, mala and mojari, made to measure.",
  },
  {
    slug: "wedding-guest", label: "Wedding Guest", priority: 1,
    match: { roles: ["guest", "groom_friend", "groom_brother", "family"], occasions: ["wedding", "reception", "sangeet"] },
    customizer: "/custom/ethnic/select_cloth/personalize",
    title: "Wedding Guest Outfits for Men",
    h1: "Wedding Guest Looks",
    intro: "Kurta jacket sets, Nehru jackets and blazers for the guests, brothers and friends of the groom.",
  },
  {
    slug: "festive", label: "Festive", priority: 2, any: true,
    match: { occasions: ["festive", "puja"], festivals: ["eid", "diwali", "durga_puja", "navratri"] },
    customizer: "/custom/ethnic/select_cloth/personalize",
    title: "Festive Wear for Men: Eid, Diwali & Puja",
    h1: "Festive Wear",
    intro: "Kurtas, pajamas and topis for Eid, Diwali and every puja, tailored to fit.",
  },
  {
    slug: "office-wear", label: "Office Wear", priority: 3,
    match: { occasions: ["office", "interview"] },
    customizer: "/custom/suit/select_cloth/personalize",
    title: "Office Wear for Men: Suits, Shirts & Trousers",
    h1: "Office Wear",
    intro: "Suits, blazers, shirts and trousers for work, cut to your measurements.",
  },
];

// ---- helpers (no imports, so both apps can use them as-is) ----

const list = (v) => (Array.isArray(v) ? v : v == null || v === "" ? [] : [v]);
const lower = (v) => String(v || "").toLowerCase();

export const departmentBySlug = (slug) => DEPARTMENTS.find((d) => d.slug === slug) || null;

export const styleBySlug = (deptSlug, styleSlug) =>
  departmentBySlug(deptSlug)?.styles.find((s) => s.slug === styleSlug) || null;

export const collectionBySlug = (slug) => COLLECTIONS.find((c) => c.slug === slug) || null;

// Piece names a product is made of (fabrics[].type or types[]).
const piecesOf = (doc) =>
  [...list(doc?.types), ...list(doc?.fabrics).map((f) => f && f.type)]
    .filter(Boolean)
    .map((p) => lower(p));

const isAccessoryDoc = (doc) => doc?.productKind === "accessory" || doc?.category === "accessory" || !!doc?.accessoryType;

export function styleMatches(style, doc) {
  const m = style.match || {};
  if (m.styleTags && list(doc?.styleTags).some((t) => m.styleTags.includes(t))) return true;
  // uniforms: the sectors a uniform is sold to
  if (m.sectors && list(doc?.sectors).some((t) => m.sectors.includes(t))) return true;
  if (m.accessoryTypes) return isAccessoryDoc(doc) && m.accessoryTypes.includes(doc.accessoryType);
  if (m.categories) {
    if (isAccessoryDoc(doc) || !m.categories.includes(lower(doc?.category))) return false;
    const pcs = piecesOf(doc);
    if (m.pieces && !m.pieces.some((p) => pcs.includes(lower(p)))) return false;
    if (m.notPieces && m.notPieces.some((p) => pcs.includes(lower(p)))) return false;
    return true;
  }
  return false;
}

// The page a product lives on: { department, style } for the first page style
// it matches (filter-only styles resolve to their parent page). Null when the
// product fits nowhere, which the admin should treat as a mistake.
export function homeOf(doc) {
  for (const d of DEPARTMENTS) {
    for (const s of d.styles) {
      if (!styleMatches(s, doc)) continue;
      const page = s.page ? s : d.styles.find((x) => x.slug === s.parent && x.page);
      return { department: d, style: page || s };
    }
  }
  return null;
}

// Every style (page or filter) a product belongs to, for facet chips.
export function stylesOf(doc) {
  const out = [];
  DEPARTMENTS.forEach((d) => d.styles.forEach((s) => {
    if (styleMatches(s, doc)) out.push({ department: d.slug, style: s.slug, page: !!s.page });
  }));
  return out;
}

// Older products carry occasions only as free text ("Diwali, Eid, Wedding"
// in `occasion`, or the `ocassion` array). Read those words as occasion and
// festival tags too, so collections fill before every product is re-tagged.
const legacyOccasionTags = (doc) =>
  [...String(doc?.occasion || "").split(","), ...list(doc?.ocassion)]
    .map((w) => lower(w).trim().replace(/[\s-]+/g, "_"))
    .filter(Boolean);

const tagValues = (doc, key) =>
  key === "occasions" || key === "festivals" ? [...list(doc?.[key]), ...legacyOccasionTags(doc)] : list(doc?.[key]);

export function collectionMatches(collection, doc) {
  const m = collection.match || {};
  const keys = Object.keys(m);
  if (!keys.length) return false;
  const hit = (key) => tagValues(doc, key).some((v) => m[key].includes(v));
  // any: one listed dimension is enough (festive)
  if (collection.any) return keys.some(hit);
  // Otherwise every dimension must match (roles AND occasions for wedding
  // guest). Products uploaded before roles existed have none; for them the
  // role check is skipped when another dimension still has to match (a
  // roles-only collection like groom wear needs real roles).
  const lenient = keys.length > 1 && !list(doc?.roles).length;
  return keys.every((k) => (k === "roles" && lenient ? true : hit(k)));
}

export const sizingOf = (doc) => homeOf(doc)?.style?.sizing || (isAccessoryDoc(doc) ? "none" : "body");

export const pathOfStyle = (deptSlug, styleSlug) => `/products/${deptSlug}/${styleSlug}`;
export const pathOfCollection = (slug) => `/collections/${slug}`;

// Admin category values each department owns, in menu order.
export const categoriesOfDepartment = (deptSlug) => {
  const d = departmentBySlug(deptSlug);
  if (!d) return [];
  const seen = new Set();
  d.styles.forEach((s) => list(s.match?.categories).forEach((c) => seen.add(c)));
  return [...seen];
};

export const departmentOfCategory = (category) =>
  DEPARTMENTS.find((d) => categoriesOfDepartment(d.slug).includes(lower(category))) || null;

// Accessory types each accessory style page owns.
export const accessoryTypesOfStyle = (styleSlug) =>
  list(styleBySlug("accessory", styleSlug)?.match?.accessoryTypes);

// Filter-only styles that can be tagged on a product of this category or type.
export const styleTagOptionsFor = (doc) => {
  const home = homeOf(doc);
  if (!home) return [];
  return home.department.styles
    .filter((s) => !s.page && s.parent === home.style.slug && s.match?.styleTags)
    .map((s) => ({ value: s.match.styleTags[0], label: s.label }));
};

// A product is listed on a style page when that page is its home, or when
// one of its style tags points at a page style of the same department.
export function isOnStylePage(doc, deptSlug, styleSlug) {
  const home = homeOf(doc);
  if (home && home.department.slug === deptSlug && home.style.slug === styleSlug) return true;
  return stylesOf(doc).some((s) => s.page && s.department === deptSlug && s.style === styleSlug);
}

// Final copy for a style page: the admin's StylePages text where written,
// the defaults above where not. The browser page and the server-rendered
// page both use this, so Google and shoppers see the same words.
export function stylePageCopy(style, saved) {
  const s = saved || {};
  const pick = (k) => (s[k] && String(s[k]).trim()) || style[k] || "";
  const title = pick("title");
  return {
    title: title.length <= 48 ? `${title} | Maviinci` : title,
    h1: pick("h1"),
    intro: pick("intro"),
    seoText: String(s.seoText || "").trim(),
    faqs: Array.isArray(s.faqs) ? s.faqs.filter((f) => f && f.q && f.a) : [],
  };
}
